import { Check, Copy } from "lucide-react";
import { isValidElement, useState, type ReactNode } from "react";
import { useLocale, type Locale } from "@/lib/i18n";

type CodeChildProps = {
  className?: string;
  children?: ReactNode;
};

export function ResourceCodeBlock({
  children,
  locale: forcedLocale,
}: {
  children?: ReactNode;
  locale?: Locale;
}) {
  const contextLocale = useLocale();
  const locale = forcedLocale ?? contextLocale;
  const [copied, setCopied] = useState(false);
  const child = isValidElement<CodeChildProps>(children) ? children : null;
  const className = child?.props.className ?? "";
  const language = /language-([\w-]+)/.exec(className)?.[1];
  const code = String(child?.props.children ?? children ?? "").replace(/\n$/, "");

  async function copyCode() {
    if (typeof navigator === "undefined" || !navigator.clipboard) return;
    await navigator.clipboard.writeText(code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }

  return (
    <div className="resource-code-shell not-prose my-9 overflow-hidden rounded-2xl border border-white/10 bg-[#09090b] shadow-[0_22px_60px_-34px_rgba(9,9,11,0.75)]">
      <div className="flex min-h-11 items-center justify-between border-b border-white/10 bg-white/[0.035] px-4">
        <div className="flex items-center gap-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
            <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
            {language ?? (locale === "es" ? "Código" : "Code")}
          </span>
        </div>
        <button
          type="button"
          onClick={copyCode}
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[11px] font-medium text-zinc-400 transition-colors hover:bg-white/8 hover:text-white"
          aria-label={locale === "es" ? "Copiar código" : "Copy code"}
        >
          {copied ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
          <span>
            {copied
              ? locale === "es"
                ? "Copiado"
                : "Copied"
              : locale === "es"
                ? "Copiar"
                : "Copy"}
          </span>
        </button>
      </div>
      <pre className="resource-code-pre m-0 overflow-x-auto bg-transparent px-5 py-5 text-[13px] leading-6 text-zinc-100 sm:px-6 sm:text-sm">
        <code className={`${className} font-mono`}>{code}</code>
      </pre>
    </div>
  );
}
